import 'vuetify/styles'
// The icon font ships with the app instead of coming from a CDN, so a page
// view sends no visitor IP address to an outside server (GDPR).
import '@mdi/font/css/materialdesignicons.css'
import { createVuetify } from 'vuetify'
import { et } from 'vuetify/locale'
import { aliases, mdi } from 'vuetify/iconsets/mdi'

// The same Estonian Vuetify as the Vue 2 app, said the way Vuetify 3 says it.
//
// Everything below is about looking like the intra the office already knows.
// Vuetify 3 changed a good many of the defaults Vuetify 2 supplied, and each one
// it changed is either a prop — and then it is said once here — or a
// measurement, and then it is in styles/v2-parity.css.
export default createVuetify({
  // Vuetify 2's palette. v3 ships a different one — its primary is #1867C0
  // rather than #1976D2, and its error a dark maroon (#B00020) rather than the
  // bright red — so the colours are restored by name.
  //
  // The material shades below the line are not theme colours in either major;
  // they are palette *classes* (`green darken-3`, `blue-grey lighten-5`), and
  // Vuetify 2 accepted them in a `color` prop as well. Vuetify 3's `color` takes
  // a theme colour or a CSS colour, and a bare "red" is then CSS red, #FF0000,
  // not the material one — so every name this app passes is declared here, with
  // Vuetify 2's material value. Their text colour Vuetify works out from the
  // contrast itself. As *classes* (`bg-red-lighten-4`) the shades need nothing:
  // vuetify/styles carries the whole material palette.
  theme: {
    defaultTheme: 'light',
    themes: {
      light: {
        dark: false,
        colors: {
          primary: '#1976D2',
          secondary: '#424242',
          accent: '#82B1FF',
          error: '#FF5252',
          info: '#2196F3',
          success: '#4CAF50',
          warning: '#FB8C00',

          black: '#000000',
          white: '#FFFFFF',
          red: '#F44336',
          green: '#4CAF50',
          blue: '#2196F3',
          grey: '#9E9E9E',
          orange: '#FF9800',
          'red-darken-2': '#D32F2F',
          'red-darken-3': '#C62828',
          'green-lighten-4': '#C8E6C9',
          'green-darken-1': '#43A047',
          'green-darken-2': '#388E3C',
          'green-darken-3': '#2E7D32',
          'blue-darken-3': '#1565C0',
          'blue-grey-lighten-5': '#ECEFF1',
          'grey-lighten-2': '#E0E0E0',
          'orange-lighten-4': '#FFE0B2',
          'orange-darken-1': '#FB8C00',
          'orange-darken-2': '#F57C00',
          'orange-darken-3': '#EF6C00',
          'deep-purple-accent-4': '#6200EA'
        }
      }
    }
  },
  // What Vuetify 3 changed its mind about since Vuetify 2, said once here rather
  // than on each component that would otherwise carry it. A component that sets
  // the prop itself still wins over any of this.
  defaults: {
    // A toast is not a modal. Vuetify 3 treats every open overlay as something
    // Back should close first — it cancels the navigation and hides the toast
    // instead — so the step back that follows a "Klienti ei leitud" toast (and
    // the reader's own Back while any toast shows) went nowhere. Vuetify 2's
    // snackbar never held Back.
    VSnackbar: { closeOnBack: false },
    // v3 makes every button inside v-card-actions a text button. The dialogs'
    // coloured buttons ("Ühenda", "Salvesta") were raised ones; the ones meant
    // flat say variant="text" themselves.
    VCardActions: { VBtn: { variant: 'elevated' } },
    // v3 draws inputs `filled`; v2 drew them underlined, and that is what every
    // screen in this app was laid out around. The search rows ask for
    // `outlined` themselves.
    //
    // `color` for the same reason: Vuetify 2 gave every input the primary colour
    // when it was active — a focused field's underline and label, a ticked
    // checkbox — and Vuetify 3 leaves an untinted control the colour of the text
    // around it.
    //
    // `itemTitle: 'text'`: Vuetify 2 read an option's label from `text` and
    // Vuetify 3 reads it from `title`. Every option list this app builds
    // ({ value, text }) was written for the first; the lists that label from
    // another field (countries, technicians) still say so themselves.
    VTextField: { variant: 'underlined', color: 'primary' },
    VTextarea: { variant: 'underlined', color: 'primary' },
    VSelect: { variant: 'underlined', color: 'primary', itemTitle: 'text' },
    VAutocomplete: { variant: 'underlined', color: 'primary', itemTitle: 'text' },
    VCombobox: { variant: 'underlined', color: 'primary', itemTitle: 'text' },
    // A table footer's own Kirjeid leheküljel select labels its options from
    // `title`, and read `text` it prints [object Object].
    VDataTableFooter: { VSelect: { itemTitle: 'title' } },
    VFileInput: { variant: 'underlined', color: 'primary' },
    VCheckbox: { color: 'primary' },
    VSwitch: { color: 'primary' },
    VRadioGroup: { color: 'primary' },
    VTabs: { color: 'primary' },
    // Vuetify 2 drew every page number as a raised white box and the current
    // one filled with the primary colour; Vuetify 3's are flat text.
    VPagination: { variant: 'elevated', activeColor: 'primary' },
    // Vuetify 2 laid a data table out as stacked cards below 600px; Vuetify 3
    // never does unless told where. The list pages' search rows are written
    // for that same switch (d-sm-table-row).
    VDataTable: { mobileBreakpoint: 'sm' },
    VDataTableServer: { mobileBreakpoint: 'sm' }
  },
  locale: {
    locale: 'et',
    fallback: 'en',
    messages: { et }
  },
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: { mdi }
  }
})
